import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useCart } from "../context/CartContext";
import { FaCog } from "react-icons/fa";
import {
  HiOutlineHome,
  HiOutlineShoppingBag,
  HiOutlineUser,
  HiOutlineUserCircle,
} from "react-icons/hi";

import {
  HiOutlineRectangleGroup,
  HiOutlineShoppingCart,
  HiOutlineChatBubbleLeftRight,
  HiOutlineArrowRightOnRectangle,
  HiOutlineChevronDown,
  HiOutlineBars3,
  HiOutlineXMark,
} from "react-icons/hi2";
import { PiArticle } from "react-icons/pi";
import SummaryApi from "../common";
import logo from "../assets/logo.png";

const Header = () => {
  const { cartCount, fetchCartCount } = useCart();
  const [userData, setUserData] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [walletPoints, setWalletPoints] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);

  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getUserId = useCallback(() => {
    const sessionData = localStorage.getItem("user");
    if (!sessionData) return null;
    try {
      const parsed = JSON.parse(sessionData);
      return parsed.user_id || parsed.id || parsed;
    } catch {
      return sessionData;
    }
  }, []);

  const fetchUserProfile = async (id) => {
    try {
      const res = await fetch(SummaryApi.userDetailsById.url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: id }),
      });
      const data = await res.json();
      setUserData(data?.data || null);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchWalletPoints = useCallback(async (userId) => {
    if (!userId) return;
    setWalletLoading(true);
    try {
      const res = await fetch(SummaryApi.wallet_points.url, {
        method: SummaryApi.wallet_points.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      const result = await res.json();
      let points = 0;
      if (result.status === true) {
        points = Number(result.data?.wallet_points) || 0;
      }
      setWalletPoints(points);
    } catch (err) {
      console.error("Wallet fetch failed:", err);
      setWalletPoints(0);
    } finally {
      setWalletLoading(false);
    }
  }, []);

  useEffect(() => {
    const userId = getUserId();
    if (userId) {
      fetchUserProfile(userId);
      fetchCartCount();
      fetchWalletPoints(userId);
    } else {
      setUserData(null);
      setWalletPoints(0);
      fetchCartCount();
    }
    window.addEventListener("cartUpdate", fetchCartCount);
    return () => window.removeEventListener("cartUpdate", fetchCartCount);
  }, [location, fetchCartCount, getUserId, fetchWalletPoints]);

const handleLogout = (e) => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }

  toast.dismiss("logout-confirm");

  toast.custom((t) => (
    <div
      className={`${
        t.visible ? 'animate-enter' : 'animate-leave'
      } flex items-center justify-between w-full max-w-md bg-white p-4 shadow-2xl rounded-[2.5rem] border-b-[6px] border-[#f84c64] pointer-events-auto`}
      style={{ minWidth: '340px' }} // Ensures it doesn't squish
    >
      <div className="flex items-center">
        {/* Left: Icon matching the reference */}
        <div className="flex items-center justify-center h-14 w-14 rounded-2xl bg-[#fff0f1] border border-[#ffdce0]">
          <div className="flex items-center justify-center h-9 w-9 rounded-full border-2 border-[#f84c64]">
             <svg className="h-5 w-5 text-[#f84c64]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
             </svg>
          </div>
        </div>

        {/* Middle: Text content */}
        <div className="ml-4 flex flex-col justify-center">
          <p className="text-[18px] font-black text-[#421010] leading-tight">
            Logged out!
          </p>
          <p className="text-[14px] font-semibold text-[#f87c8d] whitespace-nowrap">
            See you again soon 👋
          </p>
        </div>
      </div>

      {/* Right: Close Button */}
      <div className="ml-6 flex-shrink-0">
        <button
          onClick={() => toast.dismiss(t.id)}
          className="text-[13px] font-black text-[#f84c64] uppercase tracking-widest hover:opacity-70 transition-opacity pr-2"
        >
          Close
        </button>
      </div>
    </div>
  ), { 
    id: "auth-toast", 
    duration: 1000 // Display only for 1 second as requested
  });

  // State cleanup
  setIsDropdownOpen(false);
  setIsMobileMenuOpen(false);

  setTimeout(() => {
localStorage.removeItem("user");
  localStorage.removeItem("subscription_info");
  localStorage.removeItem("total_orders");
    setUserData(null);
    setWalletPoints(0);
    navigate("/login");
  }, 150); 
};

  const confirmLogout = () => {
    setIsDropdownOpen(false);
    toast.custom(
      (t) => (
        <motion.div 
          initial={{ opacity: 0, y: -20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="max-w-[calc(100vw-2rem)] sm:max-w-sm w-full bg-white shadow-2xl rounded-3xl p-6 border-b-4 border-rose-500 pointer-events-auto relative z-[9999]"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-rose-50 flex items-center justify-center text-rose-500 shrink-0">
              <HiOutlineArrowRightOnRectangle size={24} />
            </div>
            <p className="text-lg font-black text-rose-950">Logging Out?</p>
          </div>
          <div className="flex gap-3">
            {/* ✅ FIXED: Added active:scale-95, select-none, and touch-manipulation for instant mobile feedback */}
            <button
              onClick={handleLogout}
              className="flex-1 bg-rose-500 text-white font-bold py-3 rounded-xl hover:bg-rose-600 active:scale-95 transition-all touch-manipulation select-none"
            >
              Logout
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                toast.dismiss(t.id);
              }}
              className="flex-1 bg-gray-100 text-gray-500 font-bold py-3 rounded-xl hover:bg-gray-200 active:scale-95 transition-all touch-manipulation select-none"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      ),
      { id: "logout-confirm", duration: Infinity },
    );
  };

  return (
    <header className="sticky top-0 z-[50] bg-white/90 backdrop-blur-xl border-b border-rose-100">
      <div className="mx-auto px-4 lg:px-8 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src={logo} className="w-16 md:w-20 object-contain" alt="Logo" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink
            to="/"
            icon={<HiOutlineHome size={18} />}
            label="Home"
            active={location.pathname === "/"}
          />
          <NavLink
            to="/products"
            icon={<HiOutlineShoppingBag size={18} />}
            label="Shop"
            active={location.pathname === "/products"}
          />
          <NavLink
            to="/packages"
            icon={<HiOutlineRectangleGroup size={18} />}
            label="Subscribe"
            active={location.pathname === "/packages"}
          />
          {/* <NavLink
            to="/sakhi-sponsor"
            icon={<HiOutlineChatBubbleLeftRight size={18} />}
            label="Sakhi Sponsor"
            active={location.pathname === "/sakhi-sponsor"}
          /> */}
          <NavLink
            to="/articles"
            icon={<PiArticle size={18} />}
            label="Her Health"
            active={location.pathname === "/articles"}
          />
          <NavLink
            to="/contact"
            icon={<HiOutlineChatBubbleLeftRight size={18} />}
            label="Help"
            active={location.pathname === "/contact"}
          />
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          {userData && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-amber-50 to-yellow-50/80 rounded-2xl border border-amber-200/70 shadow-sm">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#F59E0B"
                strokeWidth="2"
              >
                <path d="M21 12V7C21 5.89543 20.1046 5 19 5H5C3.89543 5 3 5.89543 3 7V17C3 18.1046 3.89543 19 5 19H19C20.1046 19 21 18.1046 21 17V12Z" />
                <circle cx="13" cy="12" r="3" fill="#F59E0B" />
              </svg>
              <span className="text-sm font-black text-amber-800">
                {walletLoading ? "..." : walletPoints}
              </span>
            </div>
          )}

          {userData ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 p-1 md:pr-3 bg-rose-50/50 rounded-2xl border border-rose-100"
              >
                <div className="h-8 w-8 md:h-9 md:w-9 bg-rose-500 rounded-xl flex items-center justify-center text-white font-bold">
                  {userData.user_name?.charAt(0).toUpperCase()}
                </div>
                <span className="hidden md:block text-xs font-black text-rose-950 uppercase">
                  {userData.user_name.split(" ")[0]}
                </span>
                <HiOutlineChevronDown
                  className={`hidden md:block transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>
              <AnimatePresence>
                {isDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="absolute right-0 mt-3 w-52 bg-white border border-rose-100 rounded-2xl shadow-xl p-2"
                  >
                    <DropdownLink
                      to="/profile"
                      icon={<HiOutlineUserCircle />}
                      label="My Profile"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                    <DropdownLink
                      to="/my-orders"
                      icon={<HiOutlineRectangleGroup />}
                      label="My Orders"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                    <DropdownLink
                      to="/my-subscriptions"
                      icon={<HiOutlineRectangleGroup />}
                      label="My Subscriptions"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                    <DropdownLink
                      to="/settings"
                      icon={<FaCog />}
                      label="My Referrals"
                      onClick={() => setIsDropdownOpen(false)}
                    />
                    <button
                      onClick={confirmLogout}
                      className="w-full flex items-center gap-3 p-3 rounded-xl text-rose-950/60 hover:text-rose-600 font-bold text-sm"
                    >
                      <HiOutlineArrowRightOnRectangle size={20} /> Logout
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ) : (
            <Link to="/login" className="p-2 text-rose-400">
              <HiOutlineUser size={24} />
            </Link>
          )}

          <Link to="/cart" className="p-2 text-rose-400 relative">
            <HiOutlineShoppingCart size={24} />
            {cartCount > 0 && (
              <span className="absolute top-1 right-1 bg-rose-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center border-2 border-white">
                {cartCount}
              </span>
            )}
          </Link>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-rose-500 bg-rose-50 rounded-xl"
          >
            {isMobileMenuOpen ? (
              <HiOutlineXMark size={28} />
            ) : (
              <HiOutlineBars3 size={28} />
            )}
          </button>
        </div>
      </div>

      {/* ── MOBILE MENU ── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-rose-50 overflow-hidden"
          >
            <div className="flex flex-col p-4 gap-2">
              <MobileNavLink
                to="/"
                label="Home"
                icon={<HiOutlineHome />}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <MobileNavLink
                to="/products"
                label="Shop"
                icon={<HiOutlineShoppingBag />}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <MobileNavLink
                to="/packages"
                label="Subscribe"
                icon={<HiOutlineRectangleGroup />}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              {/* <MobileNavLink
                to="/sakhi-sponsor"
                label="Sakhi Sponsor"
                icon={<HiOutlineChatBubbleLeftRight />}
                onClick={() => setIsMobileMenuOpen(false)}
              /> */}
              <MobileNavLink
                to="/articles"
                label="Her Health"
                icon={<PiArticle  />}
                onClick={() => setIsMobileMenuOpen(false)}
              />
              <MobileNavLink
                to="/contact"
                label="Help"
                icon={<HiOutlineChatBubbleLeftRight />}
                onClick={() => setIsMobileMenuOpen(false)}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Helper Components remain the same
const MobileNavLink = ({ to, label, icon, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-4 p-4 rounded-2xl text-rose-950/70 font-bold hover:bg-rose-50 hover:text-rose-600 transition-all"
  >
    {React.cloneElement(icon, { size: 22 })}
    {label}
  </Link>
);

const NavLink = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all ${active ? "bg-rose-500 text-white shadow-md shadow-rose-200" : "text-rose-950/40 hover:text-rose-600 hover:bg-rose-50"}`}
  >
    {icon} {label}
  </Link>
);

const DropdownLink = ({ to, icon, label, onClick }) => (
  <Link
    to={to}
    onClick={onClick}
    className="flex items-center gap-3 p-3 rounded-xl text-rose-950/60 hover:text-rose-600 hover:bg-rose-50 transition-all font-bold text-sm"
  >
    {React.cloneElement(icon, { size: 20 })} {label}
  </Link>
);

export default Header;